import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-fields/constants";
import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import type {
  MovingKnown,
  MovingSolution,
  RestSolution,
  RestTarget,
} from "@/lib/tool-kit/physics-fields/mass-energy";

/** Equations with the numbers substituted, in SI. */

export function restWorking(
  target: RestTarget,
  s: RestSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  if (target === "mass") {
    return [
      "m = E / c²",
      `m = ${e(s.energy)} / (${e(SPEED_OF_LIGHT)})² = ${r(s.mass, "kg")}`,
    ];
  }
  return [
    "E = m c²",
    `E = ${e(s.mass)} × (${e(SPEED_OF_LIGHT)})² = ${r(s.energy, "J")}`,
  ];
}

export function movingWorking(
  known: MovingKnown,
  s: MovingSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const gamma = formatSig(fmt, s.gamma, Math.max(sig, 6));
  const lines = [`E₀ = m c² = ${r(s.restEnergy, "J")}`];
  if (known === "kinetic") {
    lines.push(
      `γ = 1 + K / (m c²) = 1 + ${e(s.kinetic)} / ${e(s.restEnergy)} = ${gamma}`,
      `β = √(γ² − 1) / γ = ${formatSig(fmt, s.beta, Math.max(sig, 6))},  v = β c = ${r(s.speed, "m/s")}`
    );
  } else {
    lines.push(
      `γ = 1 / √(1 − β²) = ${gamma}   (β = ${formatSig(fmt, s.beta, Math.max(sig, 6))})`,
      `K = (γ − 1) m c² = ${r(s.kinetic, "J")}`
    );
  }
  lines.push(
    `E = γ m c² = E₀ + K = ${r(s.totalEnergy, "J")}`,
    `½ m v² = ${r(s.classicalKinetic, "J")}   (the classical value; K / ½mv² = ${formatSig(fmt, s.ratio, sig)})`
  );
  return lines;
}
